import { supabaseClient } from './supabase';

export interface CarbonIntensityDataPoint {
  year: number;
  value: number;
  source: 'observed' | 'interpolated' | 'extrapolated' | 'cca';
  provider: string;
}

export interface CarbonIntensityResult {
  chartData: {
    labels: number[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor: string | string[];
      borderColor: string | string[];
      borderWidth: number;
    }>;
  };
  chartOptions: any;
  latestValue: number;
  latestYear: number;
  dataSource: string;
  providers: {
    cca: string[];
    egrid: string[];
  };
}

// Fetch CCAS data for a location
async function fetchCCASData(geofips: string): Promise<any> {
  const { data, error } = await supabaseClient
    .from('ccas')
    .select('*')
    .eq('geofips', geofips)
    .single();
  
  if (error) throw error;
  return data;
}

// Fetch eGRID data for utility matches
async function fetchEGridData(utilityMatches: string[], years: number[]): Promise<any[]> {
  const { data, error } = await supabaseClient
    .from('egrid_utilities')
    .select('year, utility_name, gco2e_per_kwh')
    .in('year', years)
    .in('utility_name', utilityMatches.filter(match => match && match.trim() !== ''));
  
  if (error) throw error;
  return data || [];
}

// Fetch LSE carbon intensity data for CCA matches
async function fetchLSECarbonIntensityData(ccaMatches: string[], years: number[]): Promise<any[]> {
  const { data, error } = await supabaseClient
    .from('lse_carbon_intensity')
    .select('year, retail_supplier, gco2e_per_kwh')
    .in('year', years)
    .in('retail_supplier', ccaMatches.filter(match => match && match.trim() !== ''));
  
  if (error) throw error;
  return data || [];
}

// Interpolate missing values
function interpolateValues(years: number[], values: (number | null)[]): (number | null)[] {
  const interpolated = [...values];
  
  for (let i = 0; i < years.length; i++) {
    if (interpolated[i] === null || interpolated[i] === undefined) {
      // Find previous and next non-null values
      let prevIndex = -1;
      let nextIndex = -1;
      
      for (let j = i - 1; j >= 0; j--) {
        if (interpolated[j] !== null && interpolated[j] !== undefined) {
          prevIndex = j;
          break;
        }
      }
      
      for (let j = i + 1; j < years.length; j++) {
        if (interpolated[j] !== null && interpolated[j] !== undefined) {
          nextIndex = j;
          break;
        }
      }
      
      if (prevIndex !== -1 && nextIndex !== -1) {
        const prevVal = interpolated[prevIndex]!;
        const nextVal = interpolated[nextIndex]!;
        const prevYear = years[prevIndex];
        const nextYear = years[nextIndex];
        const currentYear = years[i];
        
        interpolated[i] = prevVal + (nextVal - prevVal) * (currentYear - prevYear) / (nextYear - prevYear);
      }
    }
  }
  
  return interpolated;
}

// Calculate linear regression from data points
// Returns { slope, intercept } for the line y = slope * x + intercept
function calculateLinearRegression(points: Array<{year: number, value: number}>): {slope: number, intercept: number} {
  if (points.length < 2) {
    return { slope: 0, intercept: points.length === 1 ? points[0].value : 0 };
  }
  
  const n = points.length;
  const sumX = points.reduce((sum, p) => sum + p.year, 0);
  const sumY = points.reduce((sum, p) => sum + p.value, 0);
  const sumXY = points.reduce((sum, p) => sum + p.year * p.value, 0);
  const sumX2 = points.reduce((sum, p) => sum + p.year * p.year, 0);
  
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  return { slope, intercept };
}

// Check if most recent years are extrapolated and recalculate using linear regression
function recalculateExtrapolatedWithRegression(
  years: number[], 
  extrapolated: (number | null)[], 
  observedValues: (number | null)[]
): (number | null)[] {
  const result = [...extrapolated];
  
  // Find the last observed year
  let lastObservedIndex = -1;
  for (let i = years.length - 1; i >= 0; i--) {
    if (observedValues[i] !== null && observedValues[i] !== undefined) {
      lastObservedIndex = i;
      break;
    }
  }
  
  if (lastObservedIndex === -1) {
    return result; // No observed data
  }
  
  // Check if there are extrapolated values after the last observed year
  let hasExtrapolatedAfterObserved = false;
  for (let i = lastObservedIndex + 1; i < years.length; i++) {
    if (result[i] !== null && result[i] !== undefined) {
      hasExtrapolatedAfterObserved = true;
      break;
    }
  }
  
  if (!hasExtrapolatedAfterObserved) {
    return result; // No extrapolated values after observed data
  }
  
  // Find the most recent 5 observed years
  const observedPoints: Array<{year: number, value: number}> = [];
  for (let i = 0; i <= lastObservedIndex; i++) {
    if (observedValues[i] !== null && observedValues[i] !== undefined) {
      observedPoints.push({
        year: years[i],
        value: observedValues[i] as number
      });
    }
  }
  
  // Get the most recent 5 observed points
  const recentObservedPoints = observedPoints.slice(-5);
  
  if (recentObservedPoints.length < 2) {
    return result; // Need at least 2 points for regression
  }
  
  // Calculate linear regression
  const { slope, intercept } = calculateLinearRegression(recentObservedPoints);
  
  // Recalculate extrapolated values using the regression line
  for (let i = lastObservedIndex + 1; i < years.length; i++) {
    if (result[i] !== null && result[i] !== undefined) {
      const projectedValue = slope * years[i] + intercept;
      
      // Apply reasonable bounds
      const minValue = 0;
      const maxValue = Math.max(...observedPoints.map(p => p.value)) * 1.2;
      const finalValue = Math.max(minValue, Math.min(maxValue, projectedValue));
      
      result[i] = finalValue;
    }
  }
  
  console.log('🔍 Linear Regression Recalculation:', {
    recentObservedPoints: recentObservedPoints.length,
    regression: { slope, intercept },
    lastObservedYear: years[lastObservedIndex],
    recalculatedYears: years.slice(lastObservedIndex + 1).filter((_, idx) => result[lastObservedIndex + 1 + idx] !== null)
  });
  
  return result;
}

// Extrapolate missing values using linear regression of 5 most recent years for both forward and backward projection
function extrapolateValues(years: number[], values: (number | null)[], originalObserved?: (number | null)[]): (number | null)[] {
  const extrapolated = [...values];
  
  // Find all non-null values and their indices
  const validDataPoints: Array<{index: number, year: number, value: number}> = [];
  for (let i = 0; i < years.length; i++) {
    if (values[i] !== null && values[i] !== undefined) {
      validDataPoints.push({
        index: i,
        year: years[i],
        value: values[i] as number
      });
    }
  }
  
  if (validDataPoints.length === 0) {
    return extrapolated; // No data to work with
  }
  
  // Sort by year to ensure proper order
  validDataPoints.sort((a, b) => a.year - b.year);
  
  const firstPoint = validDataPoints[0];
  const lastPoint = validDataPoints[validDataPoints.length - 1];
  
  // Get the 5 most recent observed years for linear regression
  // This will be used for BOTH forward and backward extrapolation
  const recentObservedPoints = validDataPoints.slice(-5);
  
  // Calculate linear regression from the 5 most recent years
  let regressionSlope = 0;
  let regressionIntercept = 0;
  
  if (recentObservedPoints.length >= 2) {
    const regression = calculateLinearRegression(recentObservedPoints);
    regressionSlope = regression.slope;
    regressionIntercept = regression.intercept;
  } else if (recentObservedPoints.length === 1) {
    // If only one point, use it as a flat line
    regressionIntercept = recentObservedPoints[0].value;
    regressionSlope = 0;
  }
  
  // Apply linear regression projection to all missing values (both forward and backward)
  for (let i = 0; i < years.length; i++) {
    if (extrapolated[i] === null || extrapolated[i] === undefined) {
      // Use linear regression: value = slope * year + intercept
      let projectedValue = regressionSlope * years[i] + regressionIntercept;
      
      // Ensure no negative values
      projectedValue = Math.max(0, projectedValue);
      
      // Apply reasonable upper bounds to prevent extreme values
      const maxValue = Math.max(...validDataPoints.map(p => p.value)) * 1.2; // Cap at 1.2x highest observed
      projectedValue = Math.min(maxValue, projectedValue);
      
      extrapolated[i] = projectedValue;
    }
  }
  
  // If original observed values are provided, ensure we're using the correct observed data
  // for regression calculation (in case values array has interpolated data)
  if (originalObserved) {
    // Recalculate regression using only original observed values
    const observedPoints: Array<{year: number, value: number}> = [];
    for (let i = 0; i < years.length; i++) {
      if (originalObserved[i] !== null && originalObserved[i] !== undefined) {
        observedPoints.push({
          year: years[i],
          value: originalObserved[i] as number
        });
      }
    }
    
    if (observedPoints.length > 0) {
      observedPoints.sort((a, b) => a.year - b.year);
      const recentObserved = observedPoints.slice(-5);
      
      if (recentObserved.length >= 2) {
        const regression = calculateLinearRegression(recentObserved);
        regressionSlope = regression.slope;
        regressionIntercept = regression.intercept;
        
        // Recalculate all extrapolated values using the regression from observed data only
        for (let i = 0; i < years.length; i++) {
          // Check if this was originally null (i.e., it's extrapolated)
          if (originalObserved[i] === null || originalObserved[i] === undefined) {
            let projectedValue = regressionSlope * years[i] + regressionIntercept;
            
            // Ensure no negative values
            projectedValue = Math.max(0, projectedValue);
            
            // Apply reasonable upper bounds
            const maxValue = Math.max(...observedPoints.map(p => p.value)) * 1.2;
            projectedValue = Math.min(maxValue, projectedValue);
            
            extrapolated[i] = projectedValue;
          }
        }
      } else if (recentObserved.length === 1) {
        // If only one observed point, use it as a flat line for extrapolation
        regressionIntercept = recentObserved[0].value;
        regressionSlope = 0;
        
        // Recalculate all extrapolated values
        for (let i = 0; i < years.length; i++) {
          if (originalObserved[i] === null || originalObserved[i] === undefined) {
            let projectedValue = regressionIntercept; // Flat line at the observed value
            
            // Ensure no negative values
            projectedValue = Math.max(0, projectedValue);
            
            extrapolated[i] = projectedValue;
          }
        }
      }
    }
  }
  
  console.log('🔍 Linear Regression Extrapolation:', {
    validDataPoints: validDataPoints.length,
    recentObservedPoints: recentObservedPoints.length,
    regression: { slope: regressionSlope, intercept: regressionIntercept },
    firstPoint: { year: firstPoint.year, value: firstPoint.value },
    lastPoint: { year: lastPoint.year, value: lastPoint.value },
    extrapolatedCount: extrapolated.filter((v, i) => values[i] === null || values[i] === undefined).length
  });
  
  return extrapolated;
}

export async function fetchCarbonIntensityData(geofips: string): Promise<CarbonIntensityResult> {
  try {
    // Fetch CCAS data for the location
    const ccasData = await fetchCCASData(geofips);
    
    if (!ccasData) {
      throw new Error(`No CCAS data found for geofips: ${geofips}`);
    }
    
    // Define years range (2005-2023) - historical data only, no future projections
    const years = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
    
    // Get utility matches for eGRID
    const egridMatches = [
      ccasData.egrid_match1,
      ccasData.egrid_match2,
      ccasData.egrid_match3
    ].filter(match => match && match.trim() !== '');
    
    // Get CCA matches for LSE carbon intensity
    const ccaMatches = [
      ccasData.pcl_match1,
      ccasData.pcl_match2,
      ccasData.pcl_match3
    ].filter(match => match && match.trim() !== '');
    
    // Fetch data from both sources
    const [egridData, lseData] = await Promise.all([
      fetchEGridData(egridMatches, years),
      fetchLSECarbonIntensityData(ccaMatches, years)
    ]);
    
    // Process eGRID data
    const egridValues: (number | null)[] = years.map(year => {
      const yearData = egridData.find(d => d.year === year);
      return yearData ? yearData.gco2e_per_kwh : null;
    });
    
    // Process LSE data
    const lseValues: (number | null)[] = years.map(year => {
      const yearData = lseData.find(d => d.year === year);
      return yearData ? yearData.gco2e_per_kwh : null;
    });
    
    // Interpolate missing eGRID values
    const egridInterpolated = interpolateValues(years, egridValues);
    
    // Extrapolate missing eGRID values
    // Pass original observed values to check if most recent years are extrapolated
    const egridExtrapolated = extrapolateValues(years, egridInterpolated, egridValues);
    
    // Create datasets for chart - single column chart with one data source per year
    const datasets = [];
    
    // Create a single dataset that combines all sources with CCA taking precedence
    const combinedData = years.map(year => {
      const yearIndex = years.indexOf(year);
      
      // Check if CCA data exists for this year
      const ccaValue = lseValues[yearIndex];
      if (ccaValue !== null && ccaValue !== undefined) {
        return {
          value: ccaValue,
          source: 'cca',
          provider: `${ccasData.load_serving_entity || 'CCA'} - Power Content Label`
        };
      }
      
      // Check if eGRID observed data exists
      const egridObservedValue = egridValues[yearIndex];
      if (egridObservedValue !== null && egridObservedValue !== undefined) {
        return {
          value: egridObservedValue,
          source: 'observed',
          provider: `${egridMatches[0] || 'eGRID Utility'} - EIA-861 & eGRID`
        };
      }
      
      // Check if interpolated data exists
      const interpolatedValue = egridInterpolated[yearIndex];
      if (interpolatedValue !== null && interpolatedValue !== undefined) {
        return {
          value: interpolatedValue,
          source: 'interpolated',
          provider: 'Interpolated'
        };
      }
      
      // Use extrapolated data
      const extrapolatedValue = egridExtrapolated[yearIndex];
      return {
        value: extrapolatedValue || 0,
        source: 'extrapolated',
        provider: 'Extrapolated'
      };
    });
    
    // Get unique provider names for legend
    const ccaProviders = Array.from(new Set(combinedData.filter(d => d.source === 'cca').map(d => d.provider)));
    const egridProviders = Array.from(new Set(combinedData.filter(d => d.source === 'observed').map(d => d.provider)));
    
    // Create single dataset with thick bars and dynamic colors based on data source
    const singleDataset = {
      label: 'Carbon Intensity',
      data: combinedData.map(d => d.value),
      backgroundColor: combinedData.map(d => {
        switch (d.source) {
          case 'cca': return 'rgba(16, 185, 129, 0.8)'; // Green for CCA
          case 'observed': return 'rgba(59, 130, 246, 0.8)'; // Blue for eGRID
          case 'interpolated': return 'rgba(245, 158, 11, 0.8)'; // Yellow for interpolated
          case 'extrapolated': return 'rgba(239, 68, 68, 0.8)'; // Red for extrapolated
          default: return 'rgba(107, 114, 128, 0.8)'; // Gray fallback
        }
      }),
      borderColor: combinedData.map(d => {
        switch (d.source) {
          case 'cca': return 'rgba(16, 185, 129, 1)';
          case 'observed': return 'rgba(59, 130, 246, 1)';
          case 'interpolated': return 'rgba(245, 158, 11, 1)';
          case 'extrapolated': return 'rgba(239, 68, 68, 1)';
          default: return 'rgba(107, 114, 128, 1)';
        }
      }),
      borderWidth: 1
    };
    
    datasets.push(singleDataset);
    
    // Chart options
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false // Disable Chart.js legend since we have custom legend
        },
        title: {
          display: true,
          text: 'Carbon Intensity of Electricity (gCO₂e/kWh)'
        },
        tooltip: {
          callbacks: {
            title: (context: any) => `Year: ${context[0].label}`,
            label: (context: any) => {
              const dataPoint = combinedData[context.dataIndex];
              return [
                `Carbon Intensity: ${dataPoint.value.toFixed(2)} gCO₂e/kWh`,
                `Source: ${dataPoint.provider}`
              ];
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Carbon Intensity of Electricity (gCO₂e/kWh)'
          },
          ticks: {
            callback: (value: any) => Number(value).toLocaleString()
          }
        },
        x: {
          title: {
            display: true,
            text: 'Year'
          }
        }
      }
    };
    
    // Find latest value from combined data (only up to 2023)
    const latestYear = Math.max(...years.filter(year => year <= 2023));
    const latestYearIndex = years.indexOf(latestYear);
    const latestDataPoint = combinedData[latestYearIndex];
    const latestValue = latestDataPoint.value;
    const latestProvider = latestDataPoint.provider;
    
    return {
      chartData: {
        labels: years,
        datasets
      },
      chartOptions,
      latestValue: Math.round(latestValue * 100) / 100,
      latestYear,
      dataSource: latestProvider,
      providers: {
        cca: ccaProviders,
        egrid: egridProviders
      }
    };
    
  } catch (error) {
    console.error('Error fetching carbon intensity data:', error);
    throw error;
  }
}
